import { Typography, Tooltip } from '@material-ui/core'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import effects from 'app/effects/effects'
import clsx from 'clsx'
import { getDateAndTimeUTC } from 'commons/date'
import { PlanId, getPlanType, isPlanFreeTrialByPlanId } from 'commons/plan'
import * as R from 'ramda'
import React, { useState, useEffect } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useTableFetch } from 'commons/reactTableFetch'

import { PlanBillingCard } from './PlanBillingCard'
import { PlanBillingTable } from './PlanBillingTable'
import { usePlanBillingContext } from '../usePlanBillingContext'
import useUsageHistory, {
  ALERT_APP,
  ALERT_BEACON,
  ALERT_TRANSACTION,
  ALERT_EMAIL,
  ALERT_SMS,
  ALERT_BEGINNING,
} from './useUsageHistory'
import moment from 'moment'
import { useAppName } from 'commons/usePartner'
import CircularProgress from '@material-ui/core/CircularProgress'

const TooltipUsageCount = withStyles({
  tooltip: {
    fontSize: '1.4rem !important',
    maxWidth: 'none !important',
  },
})(Tooltip)

const useStyles = makeStyles((theme) => ({
  textPositive: {
    color: theme.palette.billing.positive,
  },
  textNegative: {
    color: theme.palette.negative.main,
  },
  tooltipBox: {
    width: '430px !important',
    padding: '5px',
    minHeight: '20px !important',
    verticalAlign: 'left',
  },
}))

const TransactionType = {
  BEGINNING: ALERT_BEGINNING,
  ALERT: ALERT_APP,
  BEACON_ALERT: ALERT_BEACON,
  TRANSACTION: ALERT_TRANSACTION,
  SMS: ALERT_SMS,
  EMAIL: ALERT_EMAIL,
}

const getTransactionDescription = ({ getTransactionProp, formatMessage }) => {
  if (R.equals(getTransactionProp('type'), TransactionType.ALERT)) {
    return formatMessage({
      id: 'planAndBilling.usageHistory.table.general.sentNotification.label',
    })
  }

  if (R.equals(getTransactionProp('type'), TransactionType.BEACON_ALERT)) {
    return formatMessage({
      id: 'planAndBilling.usageHistory.table.beacon.sentNotification.label',
    })
  }

  if (
    R.equals(getTransactionProp('type'), TransactionType.TRANSACTION) &&
    R.equals(getTransactionProp('description'), TransactionType.TRANSACTION)
  ) {
    return formatMessage({
      id: 'planAndBilling.usageHistory.table.description.transactions',
    })
  }

  if (
    R.equals(getTransactionProp('type'), TransactionType.TRANSACTION) &&
    R.equals(getTransactionProp(['plan', 'id']), PlanId.FREE_TRIAL)
  ) {
    return formatMessage({
      id: 'planAndBilling.usageHistory.table.startedFreeTrial.label',
    })
  }

  if (
    R.includes(getTransactionProp('action'), [
      TransactionAction.DOWNGRADE,
      TransactionAction.UPGRADE,
    ])
  ) {
    return formatMessage(
      {
        id: getDescriptionLabel(getTransactionProp('action')),
      },
      {
        name: getTransactionProp('description'),
      },
    )
  }

  if (R.equals(getTransactionProp('type'), TransactionType.BEGINNING)) {
    return '-'
  }

  return getTransactionProp(['plan', 'name'])
}

const getTransactionDate = ({ getTransactionProp, formatMessage }) => {
  const { date } = getDateAndTimeUTC(getTransactionProp('createdAt'))
  return R.equals(getTransactionProp('type'), TransactionType.BEGINNING)
    ? formatMessage({
        id: 'planAndBilling.usageHistory.table.beginning.label',
      })
    : date
}

const getTransactionSubscriptionTerm = ({
  getTransactionProp,
  formatMessage,
}) => {
  if (
    getTransactionProp('type') !== TransactionType.TRANSACTION ||
    isPlanFreeTrialByPlanId(getTransactionProp(['plan', 'id']))
  ) {
    return '-'
  }

  return formatMessage(
    {
      id: 'planAndBilling.usageHistory.table.subscriptionTerm.type',
    },
    {
      type: getPlanType(getTransactionProp(['plan', 'id'])),
    },
  )
}

const formatUsageHistory = ({ data = [] }, formatMessage) => {
  return data.map((transaction) => {
    const getTransactionProp = R.ifElse(
      R.is(Array),
      R.pathOr('', R.__, transaction),
      R.propOr('', R.__, transaction),
    )

    return {
      date: getTransactionDate({ getTransactionProp, formatMessage }),
      description: getTransactionDescription({
        getTransactionProp,
        formatMessage,
      }),
      subscriptionTerm: getTransactionSubscriptionTerm({
        getTransactionProp,
        formatMessage,
      }),
      earned: getTransactionProp('earned'),
      action: getTransactionProp('action'),
      burned: getTransactionProp('burned'),
      balance: getTransactionProp('balance'),
      entityType: R.prop('entityType', transaction),
    }
  })
}
const fetchData = (page) => {
  return effects.apis.getUsageHistory({
    page: page.currentPage,
    limit: page.pageSize,
  })
}
export function PlanBillingUsageHistoryPanel() {
  const { formatMessage } = useIntl()
  const { state: planBillingState } = usePlanBillingContext()
  const tableFetch = useTableFetch(fetchData)
  const {
    pageInfo,
    refetchData,
    defaultPageSize,
    onFetchData,
    showPagination,
  } = tableFetch

  React.useEffect(() => {
    if (R.prop('fetchedAt', planBillingState)) {
      refetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [R.prop('fetchedAt', planBillingState)])

  return (
    <PlanBillingCard labelId="planAndBilling.usageHistory.label">
      <PlanBillingTable
        minRows={0}
        className={clsx('-highlight h-full overflow-hidden')}
        FilterComponent={null}
        data={formatUsageHistory(pageInfo, formatMessage)}
        total={pageInfo.totalData}
        loading={pageInfo.isLoading}
        onFetchData={onFetchData}
        defaultPageSize={defaultPageSize}
        pages={pageInfo.total}
        showPagination={showPagination}
        manual
        noDataLabel={formatMessage({
          id: 'planAndBilling.usageHistory.table.empty.label',
        })}
        columns={[
          {
            Header: formatMessage({
              id: 'planAndBilling.usageHistory.table.date.label',
            }),
            accessor: 'date',
            sortable: false,
          },
          {
            Header: formatMessage({
              id: 'planAndBilling.usageHistory.table.description.label',
            }),
            accessor: 'description',
            sortable: false,
            type: 'text',
          },
          {
            Header: formatMessage({
              id: 'planAndBilling.usageHistory.table.subscriptionTerm.label',
            }),
            accessor: 'subscriptionTerm',
            sortable: false,
            type: 'text',
          },
          {
            Header: formatMessage({
              id: 'planAndBilling.usageHistory.table.earned.label',
            }),
            Cell: ({ value }) => (
              <UsageHistoryNumber type="earned" value={value} />
            ),
            accessor: 'earned',
            sortable: false,
            type: 'text',
          },
          {
            Header: formatMessage({
              id: 'planAndBilling.usageHistory.table.sent.label',
            }),
            Cell: ({ value, original }) => (
              <UsageHistorySendNumber
                type="burned"
                value={value}
                date={original.date}
                entityType={original.entityType}
              />
            ),
            accessor: 'burned',
            sortable: false,
            // type: 'text',
          },
          {
            Header: formatMessage({
              id: 'planAndBilling.usageHistory.table.balance.label',
            }),
            Cell: ({ value }) => <UsageHistoryBalance value={value} />,
            accessor: 'balance',
            className: 'flex justify-end',
            sortable: false,
            type: 'text',
          },
        ]}
      />
    </PlanBillingCard>
  )
}

const UsageHistoryBalance = ({ value }) => {
  const classes = useStyles()
  return (
    <Typography
      className={clsx({
        [classes.textNegative]: R.lt(value, 0),
      })}
    >
      {R.lt(value, 0)
        ? `(${Math.abs(value).toLocaleString()})`
        : value.toLocaleString()}
    </Typography>
  )
}

const UsageHistoryNumber = ({ value, type }) => {
  const classes = useStyles()
  const typeEquals = R.equals(type)
  return (
    <Typography
      className={clsx({
        [classes.textPositive]: typeEquals('earned'),
        [classes.textNegative]: typeEquals('burned'),
      })}
    >
      {value ? value.toLocaleString() : '-'}
    </Typography>
  )
}

const UsageHistorySendNumber = ({ value, type, date, entityType }) => {
  const classes = useStyles()
  const typeEquals = R.equals(type)
  const appName = useAppName()
  const [open, setOpen] = useState(false)
  const {
    isLoading,
    values,
    error,
    getUsageHistoryCount,
    clearUsageHistoryState,
  } = useUsageHistory()

  const handleClose = () => {
    setOpen(false)
    clearUsageHistoryState()
  }

  const handleOpen = () => {
    setOpen(value > 0)
  }

  useEffect(() => {
    if (open) {
      const dateNewFormat = moment(date).format('YYYY-MM-DD')
      getUsageHistoryCount(dateNewFormat, entityType)
    }
  }, [open, getUsageHistoryCount, date, entityType])

  return (
    <TooltipUsageCount
      open={open}
      onClose={handleClose}
      onOpen={handleOpen}
      title={
        <div className={classes.tooltipBox}>
          {isLoading ? (
            <div className="text-center">
              <CircularProgress color="secondary" />
            </div>
          ) : error ? (
            <div className="text-center">
              <FormattedMessage id="error.form.notFound" />
            </div>
          ) : (
            R.map((item) => {
              return (
                <div className="flex justify-start">
                  <Typography className="mx-2 w-1/3">
                    {R.includes(item.type, [
                      TransactionType.ALERT,
                      TransactionType.BEACON_ALERT,
                    ]) ? (
                      <FormattedMessage
                        id={item.labelId}
                        values={{ appName }}
                      />
                    ) : (
                      <FormattedMessage id={item.labelId} />
                    )}
                  </Typography>

                  <Typography className="px-4">{item.sent}</Typography>
                  {item.sent > 0 && (
                    <Typography className="px-4">
                      <FormattedMessage
                        id="usageHistory.charged.label"
                        values={{ burned: item.burned }}
                      />
                    </Typography>
                  )}
                </div>
              )
            }, values)
          )}
        </div>
      }
      placement="bottom"
      arrow
    >
      <Typography
        className={clsx({
          [classes.textPositive]: typeEquals('earned'),
          [classes.textNegative]: typeEquals('burned'),
        })}
      >
        {value ? value.toLocaleString() : '-'}
      </Typography>
    </TooltipUsageCount>
  )
}

export const TransactionAction = {
  DOWNGRADE: 'DOWNGRADE',
  UPGRADE: 'UPGRADE',
  RENEW: 'RENEW',
}

export const getDescriptionLabel = (action) => {
  if (action === TransactionAction.UPGRADE) {
    return 'planAndBilling.usageHistory.table.description.upgradeTo'
  }
  return 'planAndBilling.usageHistory.table.description.downgradeTo'
}
